import React from 'react';
import { useParams } from 'react-router';
import { Grid, GridColumn, GridRow, Gutter, useApi, Layout } from '@axiom/ui';
import { CandidateProfileIssuesUtils } from '@axiom/utils';

import { ProfileHeader } from '../ProfileHeader/ProfileHeader';
import { ActionNeededList } from '../ActionNeededList/ActionNeededList';
import { ProfileSummary } from '../ProfileSummary/ProfileSummary';
import { Experiences } from '../Experiences/Experiences';
import { Education } from '../Education/Education';
import { Accomplishments } from '../Accomplishments/Accomplishments';
import { Languages } from '../Languages/Languages';
import { LegalTechSkills } from '../LegalTechSkills/LegalTechSkills';
import { AvailabilityPreferences } from '../AvailabilityPreferences/AvailabilityPreferences';
import { ProfileTalentPartner } from '../ProfileTalentPartner/ProfileTalentPartner';
import { TalentReputationUsefulLinks } from '../TalentReputationUsefulLinks/TalentReputationUsefulLinks';
import { UsefulLinks } from '../UsefulLinks/UsefulLinks';
import { UserApi } from '../../api/user-api';
import { CandidateApi } from '../../api/candidate-api';
import { CandidateUtil } from '../../utils/candidate-util';
import { CandidateOpportunitiesLegacyApi } from '../../api/candidate-opportunities-legacy-api';
import { CandidateProfileIssuesApi } from '../../api/candidate-profile-issues-api';
import { FeatureVisibilityUtil } from '../../utils/feature-visibility-util';
import { PerformanceFeedback } from '../PerformanceFeedback/PerformanceFeedback';
import { ProfileHighlights } from '../ProfileHighlights/ProfileHighlights';
import { TextMessageAlertsBanner } from '../TextMessageAlertsBanner/TextMessageAlertsBanner';

export type ProfileViewProps = {
  name?: string;
};

export const ProfileView = ({ name = 'PROFILE_VIEW' }: ProfileViewProps) => {
  const { candidateId: urlCandidateId } = useParams();
  const [{ data: user }] = useApi(UserApi.readUser());
  const candidateId = CandidateUtil.reconcileUserCandidateId(
    user,
    urlCandidateId
  );
  const [
    { data: candidate },
    { data: candidateOpportunities },
    { data: profileIssues },
  ] = useApi(
    CandidateApi.readCandidate(candidateId),
    CandidateOpportunitiesLegacyApi.readOpportunitiesByCandidateId(candidateId),
    CandidateProfileIssuesApi.readCandidateProfileIssues(candidateId)
  );

  return (
    <Grid name={name}>
      <GridRow>
        {FeatureVisibilityUtil.shouldSeeLimitedFeatureSetBasedOnProfileStatus(
          candidate
        ) ? (
          <GridColumn
            largeScreenWidth={2}
            tabletWidth={2}
            name="profile-view-empty-left-column"
          />
        ) : (
          <GridColumn
            largeScreenWidth={4}
            tabletWidth={4}
            mobileWidth={12}
            name="profile-view-employee-items"
          >
            <Layout direction="vertical" verticalGutter="16px" stretched>
              <AvailabilityPreferences />
              <ProfileTalentPartner candidate={candidate} />
              <UsefulLinks />
              <TalentReputationUsefulLinks />
            </Layout>
            <Gutter bottom="16px" />
          </GridColumn>
        )}
        <GridColumn largeScreenWidth={8} tabletWidth={8} mobileWidth={12}>
          <ProfileHeader candidate={candidate} />
          <Gutter bottom="16px" />
          <TextMessageAlertsBanner candidate={candidate} />
          {CandidateProfileIssuesUtils.shouldShowActionNeededList(
            candidate,
            candidateOpportunities,
            profileIssues.issues
          ) && (
            <Gutter bottom="16px" name="profile-view-action-needed-list">
              <ActionNeededList candidate={candidate} />
            </Gutter>
          )}
          {!FeatureVisibilityUtil.shouldSeeLimitedFeatureSetBasedOnProfileStatus(
            candidate
          ) && (
            <Gutter bottom="16px">
              <ProfileHighlights candidate={candidate} />
            </Gutter>
          )}
          <ProfileSummary candidateId={candidate.id} />
          <Gutter bottom="16px" />
          <PerformanceFeedback candidate={candidate} />
          <Gutter bottom="16px" />
          <Experiences candidateId={candidate.id} />
          <Gutter bottom="16px" />
          <LegalTechSkills candidateId={candidate.id} />
          <Gutter bottom="16px" />
          <Education candidate={candidate} issues={profileIssues.issues} />
          <Gutter bottom="16px" />
          <Accomplishments candidate={candidate} />
          <Gutter bottom="16px" />
          <Languages candidateId={candidate.id} />
        </GridColumn>
      </GridRow>
    </Grid>
  );
};
